// frontend/src/components/Texpact/MessageList.jsx

import React, { useState, useEffect } from 'react';
import { shipmentsAPI } from '../../api/client';
import DataTable from '../Common/DataTable';
import Pagination from '../Common/Pagination';
import './MessageList.css';

// ─── Detail Modal ──────────────────────────────────────────────────────────────
const DetailModal = ({ msg, onClose }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="detail-modal" onClick={e => e.stopPropagation()}>
      <div className="detail-modal-header">
        <div>
          <h3>Message Detail</h3>
          <p className="detail-modal-subtitle">{msg.po_code} &mdash; {new Date(msg.created_at).toLocaleString()}</p>
        </div>
        <button className="detail-modal-close" onClick={onClose}>✕</button>
      </div>
      <div className="detail-modal-body">
        <div className="detail-row"><span className="detail-label">Status</span><span>{msg.status}</span></div>
        <div className="detail-row">
          <span className="detail-label">API Code</span>
          <span className={msg.api_knitcloth_status_code === 201 ? 'success-code' : 'error-code'}>
            {msg.api_knitcloth_status_code || '—'}
          </span>
        </div>
        <div className="detail-row"><span className="detail-label">API Status</span><span>{msg.api_knitcloth_status || '—'}</span></div>
        <div className="detail-row"><span className="detail-label">Partner From</span><span>{msg.partner_id_from || '—'}</span></div>
        <div className="detail-row"><span className="detail-label">Partner To</span><span>{msg.partner_id_to || '—'}</span></div>
        <div className="detail-row"><span className="detail-label">Supplier</span><span>{msg.supplier_name}</span></div>
        {msg.api_knitcloth_response && (
          <div className="detail-response">
            <span className="detail-label">Response Body</span>
            <pre className="detail-response-body">
              {(() => { try { return JSON.stringify(JSON.parse(msg.api_knitcloth_response), null, 2); } catch { return msg.api_knitcloth_response; } })()}
            </pre>
          </div>
        )}
      </div>
    </div>
  </div>
);

// ─── MessageList ───────────────────────────────────────────────────────────────
const MessageList = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailModal, setDetailModal] = useState(null);

  const [partnerFilter, setPartnerFilter] = useState('');
  const [supplierRestriction, setSupplierRestriction] = useState(null);
  const [poCodeInput, setPoCodeInput] = useState('');
  const [poCodeFilter, setPoCodeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => { loadMessages(); }, [partnerFilter, poCodeFilter, statusFilter, currentPage, itemsPerPage]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (partnerFilter) params.append('partnerId', partnerFilter);
      if (poCodeFilter)  params.append('poCode', poCodeFilter);
      if (statusFilter !== 'ALL') params.append('status', statusFilter);
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);

      const response = await shipmentsAPI.getAllMessages(params.toString());
      if (response.data.success) {
        setMessages(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
        setTotalItems(response.data.pagination.totalItems);
        if (response.data.supplierRestriction) setSupplierRestriction(response.data.supplierRestriction);
      }
      setError(null);
    } catch (err) {
      setError('Failed to load messages: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => { setPoCodeFilter(poCodeInput); setCurrentPage(1); };

  const statusBadge = (status, code) => {
    if (code === 201 || status === 'SUCCESS')
      return <span className="status-badge status-success">✓ Success</span>;
    if (status === 'FAILED')
      return <span className="status-badge status-failed">✗ Failed</span>;
    return <span className="status-badge status-pending">⏳ Pending</span>;
  };

  const columns = [
    { header: 'Created At',   field: 'created_at',     render: (row) => <span className="msg-date-cell">{new Date(row.created_at).toLocaleString()}</span> },
    { header: 'PO Code',      field: 'po_code',         render: (row) => <strong>{row.po_code}</strong> },
    { header: 'Supplier',     field: 'supplier_name',   render: (row) => row.supplier_name },
    { header: 'Partner From', field: 'partner_id_from', render: (row) => row.partner_id_from || '—' },
    { header: 'Partner To',   field: 'partner_id_to',   render: (row) => row.partner_id_to || '—' },
    { header: 'Status',       field: 'status',          render: (row) => statusBadge(row.status, row.api_knitcloth_status_code) },
    {
      header: 'API Response',
      field: 'api_knitcloth_status_code',
      render: (row) => row.api_knitcloth_status_code
        ? <span className={row.api_knitcloth_status_code === 201 ? 'success-code' : 'error-code'}>
            {row.api_knitcloth_status_code} — {row.api_knitcloth_status}
          </span>
        : <span className="no-response">—</span>
    },
    { header: '', field: 'actions', render: (row) => <button className="btn-detail" onClick={() => setDetailModal(row)}>🔍 Detail</button> }
  ];

  return (
    <div className="message-list">
      <div className="message-filters">
        <div className="filters-row">
          {!supplierRestriction && (
            <div className="filter-group">
              <label>Partner</label>
              <select value={partnerFilter} onChange={(e) => { setPartnerFilter(e.target.value); setCurrentPage(1); }}>
                <option value="">All Partners</option>
                <option value="verdeveste-com">Verdeveste</option>
                <option value="costalongras">Costa Longras</option>
              </select>
            </div>
          )}
          <div className="filter-group filter-group--wide">
            <label>PO Code</label>
            <div className="search-row">
              <input
                type="text" className="filter-input" value={poCodeInput}
                placeholder="e.g. CP12600071"
                onChange={(e) => setPoCodeInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button className="btn-search" onClick={handleSearch}>Search</button>
            </div>
          </div>
          <div className="filter-group">
            <label>Status</label>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
              <option value="ALL">All</option>
              <option value="SUCCESS">Success</option>
              <option value="FAILED">Failed</option>
              <option value="PENDING">Pending</option>
            </select>
          </div>
          <button onClick={loadMessages} className="btn-refresh">🔄 Refresh</button>
        </div>
      </div>

      {!loading && !error && totalItems > 0 && (
        <div style={{ marginBottom: '12px', fontSize: '14px', color: 'var(--gray-600)', fontWeight: 500, padding: '0 4px' }}>
          Total: {totalItems} message{totalItems !== 1 ? 's' : ''}
        </div>
      )}

      <DataTable columns={columns} data={messages} loading={loading} error={error} onRetry={loadMessages} emptyMessage="No messages found" />

      {!loading && !error && totalItems > 0 && (
        <Pagination
          currentPage={currentPage} totalPages={totalPages} totalItems={totalItems}
          itemsPerPage={itemsPerPage} onPageChange={setCurrentPage}
          onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
        />
      )}

      {detailModal && <DetailModal msg={detailModal} onClose={() => setDetailModal(null)} />}
    </div>
  );
};

export default MessageList;